import * as React from 'react';
import { classNames, useUid } from '@/utils';

export interface SwitchProps {
  /** The HTML ID attribute to be applied to the switch element (optional). */
  id?: string;
  /** The name attribute to be applied to the switch element (optional). */
  name?: string;
  /** Whether the switch toggle should be disabled (optional). */
  disabled?: boolean;
  /** The default checked state of the switch toggle (optional). */
  defaultChecked?: boolean;
  /**
   * A function that is called when the state of the switch toggle changes (optional).
   * This function accepts the new state of the switch toggle as input.
   */
  onChange?(value: boolean): void;
  /** The label to be displayed next to the switch toggle (optional). */
  label?: React.ReactNode;
  /** The help text to be displayed below the switch toggle (optional). */
  helpText?: React.ReactNode;
  /** CSS class names to be applied to the label element (optional). */
  labelClassName?: string;
  /** CSS class names to be applied to the switch input element (optional). */
  inputClassName?: string;
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>((props, ref) => {
  const {
    id,
    label,
    name,
    disabled,
    defaultChecked = false,
    helpText,
    labelClassName,
    inputClassName,
    onChange,
  } = props;
  const [checked, setChecked] = React.useState(defaultChecked);

  const uid = useUid(id);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.currentTarget.checked);
    onChange?.(event.currentTarget.checked);
  };

  return (
    <label htmlFor={uid}>
      {label && <p className={labelClassName}>{label}</p>}
      <span className={classNames('switch', inputClassName)}>
        <input
          ref={ref}
          type="checkbox"
          name={name}
          id={uid}
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
        />
        <figure />
      </span>
      {helpText && <small>{helpText}</small>}
    </label>
  );
});

Switch.displayName = 'Switch';
